import {
  DEFAULT_CRS,
  SupportedSoilTextures,
} from '@app/_components/lf-map/mapTypes'
import { isPresent } from '@farmai-shared/LFUtils'
import { SupportedMapLayers, VIType } from '@stores/enums'
import { DateTime } from 'luxon'
import {
  ActivityResponse,
  ActivityTypeResponse,
  AssignmentResponse,
  CropResponse,
  CropzoneGeoJSON,
  CropzoneResponse,
  FarmResponse,
  FieldGeoJSON,
  FieldResponse,
  FollowResponse,
  LabelResponse,
  NotificationResponse,
  OrganizationResponse,
  OrganizationStats,
  PlaceResponse,
  SeasonResponse,
  SeasonTercileResponse,
  SoilSampleResponse,
  TemplateResponse,
  TransactionResponse,
  UserResponse,
  VIResponse,
  YieldStatsResponse,
  YieldStatValue,
} from './responseTypes'
import { userStore } from '@stores/UserStore'

export const Transformers = {
  organizationStats: (data: OrganizationStats) => ({
    totalFields: data?.total_fields || 0,
    totalCropzonesArea: data?.total_cropzones_area || 0,
    totalFieldsArea: data?.total_fields_area || 0,
    totalFieldsPerCountry: data?.total_fields_per_country || {},
  }),
  user: (data: UserResponse) => ({
    id: data.id,
    name: data.name,
    email: data.email,
    settings: {
      lang: data.lang,
      area_unit: data.area_unit,
      weight_unit: data.weight_unit,
      soil_units: data.soil_units,
    },
  }),
  farm: (data: FarmResponse) => ({
    id: data.id,
    name: data.name,
    slug: data.slug,
    phone: data.phone || '',
    location: data.location || { type: 'Point', coordinates: [0, 0] },
    address: data.address || '',
    stats: data.stats ? Transformers.organizationStats(data.stats) : null,
  }),
  organization: (data: OrganizationResponse) => ({
    id: data.id,
    name: data.name,
    slug: data.slug,
    createdAt: DateTime.fromISO(data.created_at).year,
    phone: data.phone || '',
    location: data.location || { type: 'Point', coordinates: [0, 0] },
    seed_usage_unit: data.seed_usage_unit,
    address: data.address || '',
    prodTargets: {
      dailyMin: parseFloat(data.prod_targets?.daily_min || '0'),
      dailyMax: parseFloat(data.prod_targets?.daily_max || '1'),
    },
    description: '',
    stats: data.stats ? Transformers.organizationStats(data.stats) : null,
    cropSummary: data.crop_summary
      ? data.crop_summary.map((cs) => ({
          name: cs.name,
          name_i18n: {
            en: cs.name_i18n.en,
            th: cs.name_i18n.th,
            ja: cs.name_i18n.ja,
            vi: cs.name_i18n.vi,
          },
          area: cs.area,
          total_percentage: cs.total_percentage,
          varieties: cs.varieties,
        }))
      : [],
    features: data.features,
  }),
  activity: (data: ActivityResponse) => ({
    id: data.id,
    attachments: data.attachments || [],
    cropzone: data.cropzone && Transformers.cropzone(data.cropzone),
    farm: {
      id: data.farm.id,
      name: data.farm.name,
    },
    field: data.field
      ? {
          id: data.field?.id,
          name: data.field?.name,
        }
      : null,
    endDatetime: data.end_date,
    datetime: data.start_date,
    extra: data.extra,
    status: data.status,
    note: data.note || null,
    activityType: data.activity_type,
    assignees: data.assignees,
    labels: data.labels,
    template: data.template
      ? {
          id: data.template?.id,
          name: data.template?.name,
        }
      : null,
  }),
  activityType: (data: ActivityTypeResponse) => ({
    id: data.id,
    name: data.name,
    canonical_name: data.canonical_name,
    extra_schema: data.extra_schema,
  }),
  vi: (data: VIResponse) => ({
    id: data.id,
    cropzone_id: data.cropzone_id,
    uri: data.uri,
    datetime: data.date,
    cloudy: data.cloudy,
    mean: data.mean || null,
    type: data.type.toLowerCase() as VIType,
    thresholds: {
      performance: data.thresholds?.performance || { low: null, high: null },
    },
    extra_info: data.extra_info || {
      isComposite: false,
      maskedPercentage: null,
      crs: DEFAULT_CRS,
    },
  }),
  multibandVi: (data: VIResponse, viType: SupportedMapLayers) => {
    const { cropzone_id, date, id, thresholds, uri, extra_info, cloudy } = data
    const value = {
      [VIType.EVI]: {
        type: VIType.EVI,
        mean: extra_info.__SHRINKED_POLYGON_EVI_MEAN,
        band: 15,
      },
      [VIType.NDVI]: {
        type: VIType.NDVI,
        mean: extra_info.__SHRINKED_POLYGON_NDVI_MEAN,
        band: 13,
      },
      [VIType.NDMI]: {
        type: VIType.NDMI,
        mean: extra_info.__SHRINKED_POLYGON_NDMI_MEAN,
        band: 14,
      },
    }[viType]

    if (value) {
      return {
        cropzone_id,
        type: value.type,
        datetime: date,
        id,
        cloudy,
        mean: value.mean,
        thresholds: {
          performance: thresholds.performance || { low: null, high: null },
        },
        band: value.band,
        uri,
        extra_info,
      }
    }
    return null
  },
  prodValue: (data: YieldStatValue[]) =>
    data.map((v) => ({
      label: v.label,
      value: v.value,
      year: parseInt(v.year, 10),
      month: v.month ? parseInt(v.month, 10) : null,
      week: v.week ? parseInt(v.week, 10) : null,
      day: v.day ? parseInt(v.day, 10) : null,
      average_yield: v.average_yield,
    })),
  yieldStats: (data: YieldStatsResponse) => ({
    targetProduction: data.target,
    target: data.target,
    targetMax: data.target_max,
    targetMin: data.target_min,
    actual: {
      total: data.actual.total,
      average: data.actual.average,
      percentageOfTarget: data.actual.percentage_of_target,
      averageYield: data.actual.average_yield,
      percentageOfTargetYield: data.actual.percentage_of_target_yield,
      targetYield: data.actual.target_yield,
      values: Transformers.prodValue(data.actual.values),
    },
    forecasted: {
      total: data.forecasted.total,
      values: Transformers.prodValue(data.forecasted.values),
    },
    crop: data.crop,
    labels: data.labels,
  }),
  crop: (data: CropResponse) => {
    const lang = userStore.user.settings.lang || 'en'

    return {
      id: data.id,
      name: data.name_i18n[lang] || data.name_i18n['en'] || data.name,
      variety:
        data.variety_i18n[lang] || data.variety_i18n['en'] || data.variety,
      genus: data.genus || null,
      canonical_name: data.canonical_name,
      rawName: data.name,
    }
  },
  follow: (f: FollowResponse, from?: string) => ({
    id: f.id,
    follow_method: f.follow_method ? Object.keys(f.follow_method)[0] : null,
    request_from: from || null,
    follow_method_id: f.follow_method
      ? Object.values(f.follow_method)[0]
      : null,
    organization_left: Transformers.organization(f.organization_left),
    organization_right: Transformers.organization(f.organization_right),
    status: f.status,
    datetime: f.created_at,
  }),
  place: (p: PlaceResponse) => ({
    id: p.id,
    name: p.name,
    kind: p.kind,
    type: p.type || 'Place',
    address: p.address,
    description: p.description ?? null,
    phone: p.phone ?? null,
    annual_production_target: p.annual_production_target
      ? Number(p.annual_production_target)
      : null,
    annual_production_target_unit: p.annual_production_target_unit ?? null,
    capacity: p.capacity ? Number(p.capacity) : null,
    lnglat: p.lnglat,
  }),
  template: (data: TemplateResponse) => ({
    id: data.id,
    name: data.name,
    tasks: data.tasks || [],
    tasks_count: data.tasks_count,
    assignments_count: data.assignments_count,
    created_at: DateTime.fromISO(data.created_at),
    updated_at: DateTime.fromISO(data.updated_at),
  }),
  assignment: (a: AssignmentResponse) => ({
    id: a.id,
    name: a.name,
    template_id: a.template.id,
    template_name: a.template.name,
    created_at: a.created_at,
    cropzones_count: a.cropzones_count,
  }),
  tx: (data: TransactionResponse, timezone: string) => ({
    id: data.id,
    cropzone_id: data.cropzone_id,
    source: data.source ?? undefined,
    destination: data.destination,
    datetime: data.datetime,
    timezone: timezone,
    qtyExchanged: data.qty_exchanged,
    cropQualityUnit: data.crop_quality_unit,
    extra: {
      income: typeof data.extra?.income === 'number' ? data.extra.income : null,
      crop_quality:
        typeof data.extra?.crop_quality === 'number'
          ? data.extra.crop_quality
          : null,
    },
  }),
  soilSample: (data: SoilSampleResponse) => ({
    id: data.id,
    soilTexture: data.results.soil_texture
      .toLowerCase()
      .replace(/\s+/g, '_') as SupportedSoilTextures,
    elements: data.results.elements || [],
    type: SupportedMapLayers.SOIL,
    cropzone_id: data.cropzone.id,
    location: data.location,
    datetime: data.sampled_at,
  }),
  notification: (data: NotificationResponse) => ({
    id: data.id,
    type: data.type,
    recipient_type: data.recipient_type,
    recipient_id: data.recipient_id,
    params: data.params,
    level: data.level,
    read_at: data.read_at,
    created_at: data.created_at,
    updated_at: data.updated_at,
    isRead: isPresent(data.read_at),
  }),
  lbl: (data: LabelResponse) => ({
    id: data.id,
    name: data.name,
    color: data.color,
    description: data.description,
    readOnly: data.read_only,
    organizationId: data.organization_id,
  }),
  season: (data: SeasonResponse) => {
    const userLang = userStore.user.settings.lang

    return {
      organization_id: data.organization_id,
      id: data.id,
      name: data.name,
      datetime: data.start_datetime,
      endDatetime: data.end_datetime,
      country: data.country,
      region: data.region,
      status: data.status,
      pending_plantation: data.pending_plantation,
      crop_name: data.crop_name,
      crop_name_i18n: isPresent(data.crop_name)
        ? data.crop_name_i18n[userLang] || data.crop_name_i18n['en']
        : null,
    }
  },
  field: (data: FieldResponse): FieldGeoJSON => ({
    id: data.id,
    geometry: {
      type: 'GeometryCollection',
      geometries: [data.location.centroid, data.location.boundary].filter(
        (g) => g && (g.type === 'Point' || g.type === 'Polygon')
      ),
    },
    properties: {
      id: data.id,
      farm_id: data.farm_id,
      name: data.name,
      area: data.area,
      note: data.note,
      farm: data.farm,
      createdAt: data.created_at,
      stats: data.stats,
      timezone: data.timezone,
    },
  }),
  cropzone: (data: CropzoneResponse): CropzoneGeoJSON => ({
    id: data.id,
    geometry: {
      type: 'GeometryCollection',
      geometries: [data.location.centroid, data.location.boundary].filter(
        (g) => g && (g.type === 'Point' || g.type === 'Polygon')
      ),
    },
    properties: {
      id: data.id,
      name: data.name,
      slug: data.slug,
      area: data.area,
      note: data.note,
      startDate: data.start_date,
      endDate: data.end_date || null,
      plantingDate: data.planting_date || null,
      cropProgress: (() => {
        if (data.actual?.harvesting_date) {
          const harvestDT = DateTime.fromISO(data.actual.harvesting_date)
          if (harvestDT.isValid && harvestDT.toISODate()! <= DateTime.now().toISODate()!) {
            return 'harvested'
          }
        }
        return data.crop_progress || null
      })(),
      farm: data.farm,
      field: data.field,
      createdAt: data.created_at,
      timezone: data.timezone,
      generation: data.generation,
      amountOfSeeds: data.seed_usage,
      amountOfSeeds_unit: data.seed_usage_unit,
      country: data.country_i18n,
      region: data.region_i18n,
      district: data.district_i18n,
      subdistrict: data.subdistrict_i18n,
      prevCropzone: data.prev_cropzone,
      nextCropzone: data.next_cropzone,
      archivedAt: data.archived_at || null,
      predicted: {
        maturityDate: data.predicted.maturity_date,
        maturityDateComputedAt: data.predicted.maturity_date_computed_at,
        harvest: data.predicted.harvest,
        harvestUnit: data.predicted.harvest_unit,
        harvestComputedAt: data.predicted.harvest_computed_at,
        yield: data.predicted.yield,
        yieldUnit: data.predicted.yield_unit,
        yieldComputedAt: data.predicted.yield_computed_at,
        ccs: data.predicted.ccs,
        ccsComputedAt: data.predicted.ccs_computed_at,
        ccs_unit: data.predicted.ccs_unit,
      },
      actual: {
        harvestingDate: data.actual?.harvesting_date || '',
        harvest: data.actual?.harvest || null,
        yield: data.actual?.yield || null,
      },
      activitiesCount: data.activities_count,
      activities: data.activities,
      soil_samples: data.soil_samples,
      soilSamplesCount: data.soil_samples_count,
      distance: data.distance,
      rsStoppedAt: data.rs_stopped_at || null,
      dap: data.dap || null,
      latestSoilSampleAt: data.latest_soil_sample_at,
      gdd: data.gdd || null,
    },
    traceability: {
      ...data.traceability,
      harvest_destination_places:
        data.traceability?.harvest_destination_places.map((p) => p.id) || [],
    },
    crop: Transformers.crop(data.crop),
    labels: data.labels,
    season: isPresent(data.season)
      ? {
          id: data.season.id!,
          name: data.season.name!,
          datetime: data.season.start_datetime!,
          endDatetime: data.season.end_datetime!,
        }
      : null,
  }),
  seasonTercile: (data: SeasonTercileResponse) => ({
    property: data.property,
    startDate: data.start_date,
    endDate: data.end_date,
    percentile50: data['5_years_percentile_50'],
    probabilities: data.probabilities,
    forecast: data.forecast,
    suggestion: data.suggestion ?? null,
    createdAt: data.created_at,
  }),
}
